import { useEffect, useRef } from 'react'
import { styled } from 'styled-components'
import { DSDivider } from '../Divider/Divider'
import { DSFlex } from '../Flex/Flex'
import { DSIcon } from '../Icon/Icon'
import { DSSpacer } from '../Spacer/Spacer'
import { DSText } from '../DSText/DSText'

const StyledAccordionSection = styled.div`
  width: 100%;
  position: relative;
`

const StyledAccordionContent = styled.div`
  max-height: 0;
  transition: max-height 0.5s cubic-bezier(0, 1, 0, 1);
  overflow: hidden;

  &.active {
    max-height: 10000px;
    transition: max-height 1s ease-in-out;
  }
`

const StyledAccordionSectionHeader = styled.div`
  cursor: pointer;
`

// Use an invisible element to make sure that scrollIntoView brings the whole section below the menu bar
const StyledHiddenAnchor = styled.div`
  position: absolute;
  top: -100px;
`

export type AccordionSectionProps = {
  id: string
  title: string
  open?: boolean
  children: any
  onToggle: () => void
}

const isInViewport = (headerRef: HTMLDivElement, offset = 0) => {
  const top = headerRef.getBoundingClientRect().top
  return top + offset >= 0 && top - offset <= window.innerHeight
}

export const AccordionSection = ({ onToggle, open, children, title, id }: AccordionSectionProps) => {
  const headerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open && headerRef.current) {
      // Wait for the previously opened tab to close then scroll the newly opened one in view
      setTimeout(() => {
        if (headerRef.current && !isInViewport(headerRef.current)) {
          headerRef.current.scrollIntoView({ behavior: 'smooth' })
        }
      }, 500)
    }
  }, [open, headerRef.current])

  return (
    <StyledAccordionSection id={id}>
      <StyledHiddenAnchor ref={headerRef} />
      <DSDivider />

      <DSSpacer size={16} direction="vertical" />

      <StyledAccordionSectionHeader onClick={onToggle}>
        <DSFlex justifyContent="space-between" alignItems="center" direction="row">
          <DSText variant="fluid-paragraph-200" as="h5">
            {title}
          </DSText>
          <DSIcon name={open ? 'minus' : 'plus'} size={16} />
        </DSFlex>
      </StyledAccordionSectionHeader>

      <StyledAccordionContent className={open ? 'active' : undefined}>
        <DSSpacer size={12} direction="vertical" />
        {children}
      </StyledAccordionContent>

      <DSSpacer size={16} direction="vertical" />
    </StyledAccordionSection>
  )
}
